import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { AdminNavbar } from '../custom/AdminNavbar';
import { AdminBottomNav } from './AdminBottomNav';

/**
 * AdminMobileShell
 * ----------------
 * Common layout shell for admin pages on phone/tablet/POS form factors.
 *
 *   • Desktop (≥lg):   AdminNavbar + page content (no bottom nav).
 *   • Tablet / phone:  Sticky compact top bar (back arrow • title • refresh
 *                      • printer status pill) + content padded so the
 *                      bottom nav doesn't cover the last row.
 *
 * `newOrderCount` is forwarded to the bottom nav to render the red badge
 * on the Orders tab.
 *
 * Pages keep their own internal layout — the shell only wraps it.
 */
export const AdminMobileShell = ({
  title,
  backTo,
  onRefresh,
  refreshing = false,
  rightSlot,
  showPrinterStatus = false,
  newOrderCount = 0,
  contentClassName = '',
  children,
}) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-stone-100">
      {/* Desktop / wide-tablet navbar */}
      <div className="hidden lg:block">
        <AdminNavbar title={title} />
      </div>

      {/* Compact top bar for mobile / handheld POS */}
      <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-stone-200 bg-white px-2 lg:hidden">
        <button
          type="button"
          aria-label="Back"
          data-testid="admin-shell-back"
          onClick={() => (backTo ? navigate(backTo) : navigate(-1))}
          className="inline-flex h-10 w-10 items-center justify-center rounded-full text-stone-700 hover:bg-stone-100"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="flex-1 truncate text-base font-bold text-stone-900">
          {title}
        </h1>
        {rightSlot}
        {onRefresh && (
          <button
            type="button"
            aria-label="Refresh"
            data-testid="admin-shell-refresh"
            onClick={onRefresh}
            disabled={refreshing}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full text-stone-700 hover:bg-stone-100 disabled:opacity-50"
          >
            <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        )}
      </header>

      {/* Page content. AdminRoute's wrapper provides bottom-nav clearance
          on phone/POS — pages don't need extra padding for it. */}
      <main className={contentClassName}>{children}</main>

      <AdminBottomNav newOrderCount={newOrderCount} />
    </div>
  );
};

/**
 * Page-section helper for forms / lists — gives consistent gutters across
 * all admin pages on phone / tablet.
 */
export const AdminPageContainer = ({ children, className = '' }) => (
  <div className={`mx-auto w-full max-w-5xl px-3 py-3 sm:px-4 sm:py-4 lg:px-6 lg:py-6 ${className}`}>
    {children}
  </div>
);

/** Helper button styled for big touch targets on POS / phone. */
export const AdminBackLink = ({ to = '/admin/dashboard', children = 'Dashboard' }) => (
  <Link
    to={to}
    className="hidden items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium text-stone-700 hover:bg-stone-100 lg:inline-flex"
  >
    <ArrowLeft className="h-4 w-4" />
    {children}
  </Link>
);
